import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { API, apiError, formatINR } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Plus, Edit2, Trash2, X, Building, Phone, Mail, BookOpen } from "lucide-react";
import { toast } from "sonner";
import BulkSelectBar from "../components/BulkSelectBar";

const EMPTY = { name: "", contact_person: "", phone: "", email: "", address: "", gst: "", opening_balance: 0 };

export default function Vendors() {
  const { user } = useAuth();
  const canBulkDelete = user?.role === "admin" || user?.role === "branch_head";
  const [items, setItems] = useState([]);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try { const { data } = await API.get("/vendors"); setItems(data); }
    catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(EMPTY); setModal("new"); };
  const openEdit = (v) => {
    setForm({
      name: v.name, contact_person: v.contact_person || "", phone: v.phone || "",
      email: v.email || "", address: v.address || "", gst: v.gst || "",
      opening_balance: v.opening_balance || 0,
    });
    setModal(v);
  };

  const save = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, opening_balance: parseFloat(form.opening_balance) || 0 };
      if (modal === "new") await API.post("/vendors", payload);
      else await API.put(`/vendors/${modal.id}`, payload);
      toast.success("Saved"); setModal(null); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const del = async (v) => {
    if (!window.confirm(`Delete vendor ${v.name}?`)) return;
    try {
      await API.delete(`/vendors/${v.id}`);
      toast.success("Deleted");
      load();
    } catch (err) {
      const status = err?.response?.status;
      const detail = apiError(err);
      if (status === 409) {
        if (window.confirm(`${detail}\n\nForce delete ${v.name} and all its purchases + ledger entries (stock will be reversed)?`)) {
          try {
            await API.delete(`/vendors/${v.id}?force=true`);
            toast.success(`Deleted ${v.name} with all history`);
            load();
          } catch (e2) { toast.error(apiError(e2)); }
        }
      } else {
        toast.error(detail);
      }
    }
  };

  const toggleOne = (id) => setSelected((prev) => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  const bulkDelete = async () => {
    if (!window.confirm(`Delete ${selected.size} selected vendors? This cascades — all purchases and ledger entries for those vendors will be removed and stock reversed. Cannot be undone.`)) return;
    setBulkBusy(true);
    try {
      const { data } = await API.post("/bulk-delete", { entity: "vendors", ids: [...selected], force: true });
      toast.success(`${data.deleted} deleted${data.skipped.length ? ` · ${data.skipped.length} skipped` : ""}`);
      setSelected(new Set());
      load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBulkBusy(false); }
  };

  return (
    <div className="p-4 sm:p-6 md:p-8 space-y-5" data-testid="vendors-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="overline mb-2">Suppliers</div>
          <h1 className="font-display text-3xl font-bold">Vendors</h1>
          <p className="text-sm text-slate-500 mt-1">Manage suppliers, track credit balances and open ledgers.</p>
        </div>
        <button onClick={openNew} className="btn-primary" data-testid="add-vendor-btn"><Plus className="w-4 h-4" /> New Vendor</button>
      </div>

      {canBulkDelete && (
        <BulkSelectBar
          count={selected.size}
          total={items.length}
          onClear={() => setSelected(new Set())}
          onSelectAll={() => setSelected(new Set(items.map((v) => v.id)))}
          onDelete={bulkDelete}
          busy={bulkBusy}
          entityLabel="vendors"
        />
      )}

      {loading ? <div className="text-slate-500 text-sm">Loading…</div> : items.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-xl p-16 text-center">
          <Building className="w-10 h-10 text-slate-300 mx-auto mb-3" />
          <div className="font-display text-lg mb-1">No vendors yet</div>
          <div className="text-sm text-slate-500 mb-4">Add your first supplier to record purchases and maintain a ledger.</div>
          <button onClick={openNew} className="btn-primary"><Plus className="w-4 h-4" /> Add Vendor</button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((v) => (
            <div key={v.id} className={`bg-white border rounded-xl p-5 flex flex-col ${selected.has(v.id) ? "border-[#002FA7] ring-2 ring-[#002FA7]/15" : "border-slate-200"}`} data-testid={`vendor-${v.id}`}>
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-2">
                  {canBulkDelete && (
                    <input type="checkbox" data-testid={`vendor-select-${v.id}`}
                      checked={selected.has(v.id)} onChange={() => toggleOne(v.id)}
                      className="w-4 h-4" />
                  )}
                  <div className="w-10 h-10 rounded-lg bg-[#10B981]/10 text-[#047857] flex items-center justify-center">
                    <Building className="w-5 h-5" />
                  </div>
                </div>
                <div className="flex gap-1">
                  <Link to={`/vendor/${v.id}`} className="p-1.5 rounded hover:bg-slate-100 text-slate-500 hover:text-[#002FA7]" title="Open ledger" data-testid={`open-ledger-${v.id}`}>
                    <BookOpen className="w-3.5 h-3.5" />
                  </Link>
                  <button onClick={() => openEdit(v)} className="p-1.5 rounded hover:bg-slate-100 text-slate-500 hover:text-[#002FA7]"><Edit2 className="w-3.5 h-3.5" /></button>
                  <button onClick={() => del(v)} className="p-1.5 rounded hover:bg-red-50 text-slate-500 hover:text-red-600"><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
              </div>
              <h3 className="font-display text-lg font-semibold mt-3">{v.name}</h3>
              {v.contact_person && <div className="text-sm text-slate-600 mt-1">{v.contact_person}</div>}
              <div className="mt-2 space-y-0.5 text-xs text-slate-500">
                {v.phone && <div className="flex items-center gap-1.5"><Phone className="w-3 h-3" /> {v.phone}</div>}
                {v.email && <div className="flex items-center gap-1.5"><Mail className="w-3 h-3" /> {v.email}</div>}
                {v.gst && <div className="font-mono">{v.gst}</div>}
              </div>
              <div className="mt-4 pt-4 border-t border-slate-100 flex justify-between items-center">
                <span className="overline text-[10px]">Balance due</span>
                <span className={`font-display font-bold text-lg ${v.balance > 0 ? "text-red-600" : v.balance < 0 ? "text-emerald-600" : "text-slate-400"}`}>
                  {formatINR(Math.abs(v.balance || 0))}
                  {v.balance < 0 && <span className="text-xs font-normal"> (advance)</span>}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}

      {modal && (
        <div className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4">
          <form onSubmit={save} className="bg-white rounded-xl shadow-2xl w-full max-w-xl max-h-[90vh] overflow-y-auto">
            <div className="p-5 border-b border-slate-200 flex items-center justify-between">
              <h3 className="font-display text-xl font-semibold">{modal === "new" ? "New Vendor" : "Edit Vendor"}</h3>
              <button type="button" onClick={() => setModal(null)} className="p-1 hover:bg-slate-100 rounded"><X className="w-4 h-4" /></button>
            </div>
            <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-3">
              <L label="Vendor Name *" full><input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="inp" /></L>
              <L label="Contact Person"><input value={form.contact_person} onChange={(e) => setForm({ ...form, contact_person: e.target.value })} className="inp" /></L>
              <L label="Phone"><input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="inp" /></L>
              <L label="Email"><input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="inp" /></L>
              <L label="GST Number"><input value={form.gst} onChange={(e) => setForm({ ...form, gst: e.target.value })} className="inp font-mono" /></L>
              <L label="Address" full><textarea rows={2} value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="inp resize-none" /></L>
              <L label="Opening Balance (₹)" full>
                <input type="number" step="0.01" value={form.opening_balance} onChange={(e) => setForm({ ...form, opening_balance: e.target.value })} className="inp" />
                <div className="text-xs text-slate-500 mt-1">Positive = you owe the vendor. Negative = vendor owes you (advance).</div>
              </L>
            </div>
            <div className="p-5 border-t border-slate-200 flex justify-end gap-2">
              <button type="button" onClick={() => setModal(null)} className="btn-ghost">Cancel</button>
              <button type="submit" className="btn-primary" data-testid="save-vendor-btn">Save</button>
            </div>
          </form>
        </div>
      )}
      <style>{`.inp{width:100%;border:1px solid #E2E8F0;border-radius:8px;padding:.5rem .75rem;font-size:.875rem;outline:none;font-family:inherit}.inp:focus{border-color:#002FA7;box-shadow:0 0 0 3px rgba(0,47,167,.15)}`}</style>
    </div>
  );
}
function L({ label, children, full }) { return <label className={`block ${full ? "md:col-span-2" : ""}`}><div className="text-xs font-semibold text-slate-600 mb-1.5">{label}</div>{children}</label>; }
